import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Stack,
  Alert,
  Menu,
  Breadcrumbs,
  Link,
  Avatar,
  LinearProgress,
  alpha,
  useTheme,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  People,
  EventAvailable,
  EventBusy,
  Schedule,
  AccessTime,
  CalendarToday,
  TrendingUp,
  TrendingDown,
  Assessment,
  FilterList,
  Refresh,
  GetApp,
  Person,
  WorkOutline,
  CheckCircle,
  Cancel,
  Warning,
  HourglassEmpty,
  PictureAsPdf,
  Description,
  ArrowDropDown,
  MoreVert,
  Dashboard,
  Home,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline,
  Download,
  Search,
  ArrowForward,
  ArrowUpward,
  ArrowDownward,
  MoreHoriz,
  CalendarViewDay,
  CalendarViewWeek,
  CalendarViewMonth,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Star,
  StarBorder,
  InsertChart,
  ShowChart,
  DonutSmall,
  Equalizer
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';


import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);



const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function AttendanceReportDashboard() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [reportPeriod, setReportPeriod] = useState('all');
  const [viewType, setViewType] = useState('overview');
  const [error, setError] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [chartView, setChartView] = useState('month'); // day, week, month
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  // Calculated statistics
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalPresent: 0,
    totalAbsent: 0,
    totalLate: 0,
    averageHours: 0,
    attendanceRate: 0,
    punctualityRate: 0,
    totalWorkingDays: 0,
    totalRecords: 0,
    employeeStats: [],
    dailyStats: [],
    departmentStats: [],
    trendData: []
  });

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    fetchAttendanceData();
  }, [reportPeriod, selectedYear]);

  useEffect(() => {
    if (attendanceData.length > 0) {
      applyFilters();
    }
  }, [attendanceData, selectedEmployee, selectedDepartment]);

  useEffect(() => {
    if (filteredData.length > 0) {
      calculateStatistics();
    }
  }, [filteredData]);

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      let empId = '';
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        empId = payload.employee_id || '';
      } catch (e) {}

      const response = await axios.get(`${API_URL}/api/attendance-report/years?employee_id=${empId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.years) {
        setAvailableYears(response.data.years);
        if (response.data.years.length > 0 && !response.data.years.includes(selectedYear)) {
          setSelectedYear(response.data.years[0]);
        }
      }
    } catch (e) {
    }
  };

  const fetchAttendanceData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      let empId = '';
      let company_id = '';
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        empId = payload.employee_id || '';
        company_id = payload.company_id || '';
      } catch (e) {}

      try {
        const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (cmpRes.data && cmpRes.data.data) {
          setCompanyConfig({
            dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
            timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
          });
        }
      } catch(e) {}

      setLoading(true);
      const yearParam = reportPeriod === 'yearly' ? `&year=${selectedYear}` : '';
      const response = await axios.get(
        `${API_URL}/api/attendance-report/summary?period=${reportPeriod}${yearParam}&employee_id=${empId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (Array.isArray(response.data)) {
        setAttendanceData(response.data);
      } else {
        setAttendanceData([]);
      }
      setError('');
    } catch (err) {
      setError('Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...attendanceData];


    // Apply employee filter
    if (selectedEmployee !== 'all') {
      filtered = filtered.filter(record => record.employee_id === selectedEmployee);
    }

    // Apply department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(record => record.department === selectedDepartment);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(record => 
        record.name.toLowerCase().includes(query) ||
        record.employee_id.toLowerCase().includes(query) ||
        (record.department && record.department.toLowerCase().includes(query))
      );
    }

    setFilteredData(filtered);
  };

  const calculateStatistics = () => {
    if (filteredData.length === 0) {
      setStats({
        totalEmployees: 0,
        totalPresent: 0,
        totalAbsent: 0,
        totalLate: 0,
        averageHours: 0,
        attendanceRate: 0,
        punctualityRate: 0,
        totalWorkingDays: 0,
        totalRecords: 0,
        employeeStats: [],
        dailyStats: [],
        departmentStats: [],
        trendData: []
      });
      return;
    }

    // Basic counts
    const totalPresent = filteredData.filter(r => r.status === 'present').length;
    const totalAbsent = filteredData.filter(r => r.status === 'absent').length;
    const totalLate = filteredData.filter(r => r.status === 'late').length;
    const totalRecords = filteredData.length;

    // Unique employees
    const uniqueEmployees = [...new Set(filteredData.map(r => r.employee_id))];
    const totalEmployees = uniqueEmployees.length;

    // Working days calculation
    const uniqueDates = [...new Set(filteredData.map(r => r.date))];
    const totalWorkingDays = uniqueDates.length;

    // Average hours calculation
    const recordsWithHours = filteredData.filter(r => r.total_hours && r.total_hours > 0);
    const totalHours = recordsWithHours.reduce((sum, r) => {
      const hours = typeof r.total_hours === 'number' ? r.total_hours : parseFloat(r.total_hours) || 0;
      return sum + hours;
    }, 0);
    const averageHours = recordsWithHours.length > 0 ? (totalHours / recordsWithHours.length) : 0;

    // Rates calculation
    const attendanceRate = totalRecords > 0 ? ((totalPresent + totalLate) / totalRecords) * 100 : 0;
    const punctualityRate = (totalPresent + totalLate) > 0 ? (totalPresent / (totalPresent + totalLate)) * 100 : 0;

    // Employee-wise statistics
    const employeeStats = uniqueEmployees.map(empId => {
      const empRecords = filteredData.filter(r => r.employee_id === empId);
      const empName = empRecords[0]?.name || 'Unknown';
      const department = empRecords[0]?.department || 'Unknown';
      const present = empRecords.filter(r => r.status === 'present').length;
      const absent = empRecords.filter(r => r.status === 'absent').length;
      const late = empRecords.filter(r => r.status === 'late').length;
      const total = empRecords.length;
      
      const empHours = empRecords
        .filter(r => r.total_hours && r.total_hours > 0)
        .reduce((sum, r) => sum + (typeof r.total_hours === 'number' ? r.total_hours : parseFloat(r.total_hours) || 0), 0);
      
      const avgHours = empRecords.filter(r => r.total_hours && r.total_hours > 0).length > 0 
        ? empHours / empRecords.filter(r => r.total_hours && r.total_hours > 0).length 
        : 0;

      return {
        employee_id: empId,
        name: empName,
        department,
        present,
        absent,
        late,
        total,
        attendanceRate: total > 0 ? ((present + late) / total) * 100 : 0,
        averageHours: avgHours,
        performance: total > 0 ? ((present / total) * 100) : 0
      };
    });

    // Daily statistics
    const dailyStats = uniqueDates.map(date => {
      const dayRecords = filteredData.filter(r => r.date === date);
      const present = dayRecords.filter(r => r.status === 'present').length;
      const absent = dayRecords.filter(r => r.status === 'absent').length;
      const late = dayRecords.filter(r => r.status === 'late').length;
      const total = dayRecords.length;

      return {
        date,
        present,
        absent,
        late,
        total,
        attendanceRate: total > 0 ? ((present + late) / total) * 100 : 0
      };
    }).sort((a, b) => new Date(a.date) - new Date(b.date));

    // Department statistics
    const departments = [...new Set(filteredData.map(r => r.department).filter(Boolean))];
    const departmentStats = departments.map(dept => {
      const deptRecords = filteredData.filter(r => r.department === dept);
      const present = deptRecords.filter(r => r.status === 'present').length;
      const absent = deptRecords.filter(r => r.status === 'absent').length;
      const late = deptRecords.filter(r => r.status === 'late').length;
      const total = deptRecords.length;
      
      return {
        department: dept,
        present,
        absent,
        late,
        total,
        attendanceRate: total > 0 ? ((present + late) / total) * 100 : 0,
        employees: [...new Set(deptRecords.map(r => r.employee_id))].length
      };
    });

    // Trend data for charts
    const trendData = dailyStats.map(day => ({
      date: dayjs(day.date).tz(companyConfig.timezone).format('MMM DD'),
      present: day.present,
      absent: day.absent,
      late: day.late,
      attendanceRate: day.attendanceRate,
      total: day.total
    }));

    setStats({
      totalEmployees,
      totalPresent,
      totalAbsent,
      totalLate,
      averageHours,
      attendanceRate,
      punctualityRate,
      totalWorkingDays,
      totalRecords,
      employeeStats: employeeStats.sort((a, b) => b.attendanceRate - a.attendanceRate),
      dailyStats,
      departmentStats: departmentStats.sort((a, b) => b.attendanceRate - a.attendanceRate),
      trendData
    });
  };

  const handlePeriodChange = (period) => {
    setReportPeriod(period);
  };

  const formatTime = (time) => {
    if (!time) return '-';
    if (typeof time === 'number') {
      const hours = Math.floor(time);
      const minutes = Math.round((time - hours) * 60);
      return `${hours}h ${minutes}m`;
    }
    return time.toString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late': return 'warning';
      default: return 'default';
    }
  };

  const exportToPDF = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('token');
      if (!token) { alert('No authentication token found. Please log in.'); return; }
      let empId = '';
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        empId = payload.employee_id || '';
      } catch (e) {}
      
      const yearParam = reportPeriod === 'yearly' ? `&year=${selectedYear}` : '';
      const url = `${API_URL}/api/attendance-report/export/pdf?period=${reportPeriod}${yearParam}&employee_id=${empId}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Attendance-Report-${reportPeriod}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to generate PDF report');
    } finally {
      setExportLoading(false);
    }
  };

  const topPerformers = stats.employeeStats.slice(0, 5);
  const attendanceDistribution = [
    { name: 'Present', value: stats.totalPresent, color: '#4CAF50' },
    { name: 'Absent', value: stats.totalAbsent, color: '#F44336' },
    { name: 'Late', value: stats.totalLate, color: '#FF9800' }
  ];

  const getTrendIcon = (rate) => {
    if (rate >= 90) return <ArrowUpward sx={{ color: '#4CAF50', fontSize: 14 }} />;
    if (rate >= 75) return <TrendingUp sx={{ color: '#FF9800', fontSize: 14 }} />;
    return <ArrowDownward sx={{ color: '#F44336', fontSize: 14 }} />;
  };

  const getTrendColor = (rate) => {
    if (rate >= 90) return '#4CAF50';
    if (rate >= 75) return '#FF9800';
    return '#F44336';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1,  minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
        

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary' }}>
                Attendance Report
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Monitor and analyze employee attendance patterns
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchAttendanceData}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={exportToPDF}
                disabled={exportLoading}
                sx={{ borderRadius: 2, background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}
              >
                Export Report
              </Button>
            </Box>
          </Box>

          {/* Quick Filters */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <Grid container spacing={2} alignItems="center">
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={reportPeriod}
                    label="Period"
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="yearly">Yearly</MenuItem>
                    <MenuItem value="all">All Time</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {reportPeriod === 'yearly' && (
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Year"
                      onChange={(e) => setSelectedYear(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {availableYears.map(yr => (
                        <MenuItem key={yr} value={yr}>
                          {yr} {yr === new Date().getFullYear() ? '(Current)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={reportPeriod === 'yearly' ? 2 : 3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Employee</InputLabel>
                  <Select
                    value={selectedEmployee}
                    label="Employee"
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  >
                    <MenuItem value="all">All Employees</MenuItem>
                    {[...new Set(attendanceData.map(r => ({ id: r.employee_id, name: r.name })))].map((emp) => (
                      <MenuItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            
            </Grid>
          </Paper>
        </Box>

        {/* 4 Key Metrics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Card 1: Attendance Rate */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
  sx={{
    borderRadius: 3,
    borderLeft: "5px solid #2196F3",
    boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
    borderTop: "1px solid rgba(0,0,0,0.05)",
    borderRight: "1px solid rgba(0,0,0,0.05)",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 20px rgba(0,0,0,0.08)",
    },
  }}
>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      ATTENDANCE RATE
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                      {stats.attendanceRate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: alpha('#2196F3', 0.1),
                    width: 48, 
                    height: 48 
                  }}>
                    <TrendingUp sx={{ color: '#2196F3' }} />
                  </Avatar>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.attendanceRate} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    mb: 1,
                    backgroundColor: alpha('#2196F3', 0.1),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getTrendColor(stats.attendanceRate),
                      borderRadius: 4
                    }
                  }} 
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {stats.totalPresent + stats.totalLate} of {stats.totalRecords} present
                  </Typography>
                  {getTrendIcon(stats.attendanceRate)}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 2: Present Employees */}
          <Grid item xs={12} sm={6} md={3}>
           <Card
  sx={{
    borderRadius: 3,
    borderLeft: "5px solid #4CAF50",
    boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
    borderTop: "1px solid rgba(0,0,0,0.05)",
    borderRight: "1px solid rgba(0,0,0,0.05)",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 20px rgba(0,0,0,0.08)",
    },
  }}
>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      PRESENT EMPLOYEES
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                      {stats.totalPresent}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: alpha('#4CAF50', 0.1),
                    width: 48, 
                    height: 48 
                  }}>
                    <CheckCircle sx={{ color: '#4CAF50' }} />
                  </Avatar>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <People sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Out of {stats.totalEmployees} total employees
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {stats.totalEmployees > 0 ? ((stats.totalPresent / stats.totalEmployees) * 100).toFixed(1) : '0'}% of workforce
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 3: Average Hours */}
          <Grid item xs={12} sm={6} md={3}>
            <Card
  sx={{
    borderRadius: 3,
    borderLeft: "5px solid #FF9800",
    boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
    borderTop: "1px solid rgba(0,0,0,0.05)",
    borderRight: "1px solid rgba(0,0,0,0.05)",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 20px rgba(0,0,0,0.08)",
    },
  }}
>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      AVG WORK HOURS
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                      {formatTime(stats.averageHours)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: alpha('#FF9800', 0.1),
                    width: 48, 
                    height: 48 
                  }}>
                    <AccessTime sx={{ color: '#FF9800' }} />
                  </Avatar>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Per day per employee
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Based on {stats.totalWorkingDays} working days
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Card 4: Punctuality Rate */}
          <Grid item xs={12} sm={6} md={3}>
           <Card
  sx={{
    borderRadius: 3,
    borderLeft: "5px solid #9C27B0",
    boxShadow: "0 8px 16px rgba(0,0,0,0.04)",
    borderTop: "1px solid rgba(0,0,0,0.05)",
    borderRight: "1px solid rgba(0,0,0,0.05)",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 20px rgba(0,0,0,0.08)",
    },
  }}
>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      PUNCTUALITY RATE
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                      {stats.punctualityRate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: alpha('#9C27B0', 0.1),
                    width: 48, 
                    height: 48 
                  }}>
                    <EventAvailable sx={{ color: '#9C27B0' }} />
                  </Avatar>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.punctualityRate} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    mb: 1,
                    backgroundColor: alpha('#9C27B0', 0.1),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: stats.punctualityRate >= 90 ? '#4CAF50' : '#9C27B0',
                      borderRadius: 4
                    }
                  }} 
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {stats.totalLate} late arrivals recorded
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Attendance Trend Chart */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Attendance Trend
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Daily attendance pattern over time
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={chartView}
                  exclusive
                  onChange={(e, newView) => newView && setChartView(newView)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 2,
                      px: 2,
                      py: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: '#2196F3',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#1976D2',
                        }
                      }
                    }
                  }}
                >
                
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={stats.trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" stroke="#666" />
                    <YAxis stroke="#666" />
                    <RechartsTooltip 
                      contentStyle={{ 
                        borderRadius: 8,
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value, name) => {
                        if (name === 'attendanceRate') return [`${value.toFixed(1)}%`, name];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="present" 
                      name="Present" 
                      fill="#4CAF50" 
                      radius={[4, 4, 0, 0]}
                      barSize={20}
                    />
                    <Bar 
                      dataKey="late" 
                      name="Late" 
                      fill="#FF9800" 
                      radius={[4, 4, 0, 0]}
                      barSize={20}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="attendanceRate" 
                      name="Attendance Rate %" 
                      stroke="#2196F3" 
                      strokeWidth={2}
                      dot={{ fill: '#2196F3', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Distribution Pie Chart */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Attendance Distribution
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status breakdown
                  </Typography>
                </Box>
                <DonutSmall sx={{ color: 'action.active' }} />
              </Box>
              <Box sx={{ height: 250, mb: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={attendanceDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={(entry) => `${entry.name}: ${entry.value}`}
                    >
                      {attendanceDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value) => [value, 'Records']}
                      contentStyle={{ borderRadius: 8 }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1}>
                {attendanceDistribution.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                      <Typography variant="body2">{item.name}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.value} records
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

     
      </Container>
    </Box>
  );
}

export default AttendanceReportDashboard;